import type { InstallationPathParams, UpdateInstallationBody } from '@si/types';
import { postForm } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

type Params = InstallationPathParams & UpdateInstallationBody & { planFile?: File };

export function useUpdateInstallation() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({
      projectId,
      installationId,
      formData,
    }: {
      projectId: string;
      installationId: string;
      formData: FormData;
    }) =>
      postForm({
        url: `/projects/${projectId}/installations/${installationId}`,
        body: formData,
      }),
    onSuccess: (_, { projectId, installationId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallations(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInstallationMap(projectId, installationId),
      });
    },
  });

  const updateInstallation = useCallback(
    (params: Params) => {
      const { projectId, installationId, name, zoom, status, planFile } = params;
      const formData = new FormData();
      if (name) {
        formData.append(
          'installationData',
          JSON.stringify({
            installationName: name,
            zoom,
            status,
          })
        );
      }

      if (planFile) {
        formData.append('installationMap', planFile);
      }

      mutate({ projectId, installationId, formData });
    },
    [mutate]
  );

  return {
    updateInstallation,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
